import { MaybeUndef, Undef } from 'types/UtilityTypes';

import {
  ALJEX_RATE_TYPE_CODE_TO_LABEL,
  ALJEX_RATE_TYPE_LABELS,
  ALJEX_RATE_TYPE_LABEL_TO_CODE,
  transportTypes,
} from './constants';

// Normalizes a transport type string to a valid Aljex transport type value code.
// Handles AI suggestions and existing load data that may contain labels instead of value codes.
export const normalizeAljexTransportType = (
  transportType: Undef<string>
): string => {
  if (!transportType) return '';

  const normalizedInput = transportType.trim().toUpperCase();

  const transportTypeValueMatch = transportTypes.find(
    (t) => t.value.toUpperCase() === normalizedInput
  );
  if (transportTypeValueMatch) return transportTypeValueMatch.value;

  const transportTypeLabelMatch = transportTypes.find(
    (t) => t.label.toUpperCase() === normalizedInput
  );
  if (transportTypeLabelMatch) return transportTypeLabelMatch.value;

  // Fallbacks for common variations
  if (normalizedInput.includes('VAN')) return 'V';
  if (normalizedInput.includes('REEFER')) return 'R';
  if (normalizedInput.includes('FLATBED')) return 'F';

  return '';
};

// Normalizes a rate type string to a valid Aljex rate type label.
// Maps LLM canonical values (Flat, Distance, CWT, Tons) and TMS codes (F, M, C) to Aljex labels.
export const normalizeAljexRateType = (rateType: Undef<string>): string => {
  if (!rateType) return '';

  const normalizedInput = rateType.trim();

  if ((ALJEX_RATE_TYPE_LABELS as readonly string[]).includes(normalizedInput)) {
    return normalizedInput;
  }

  const codeLabel =
    ALJEX_RATE_TYPE_CODE_TO_LABEL[normalizedInput.toUpperCase()];
  if (codeLabel) {
    return codeLabel;
  }

  const normalizedUpper = normalizedInput.toUpperCase();
  if (normalizedUpper === 'FLAT' || normalizedUpper === 'FLAT RATE') {
    return 'Flat Rate';
  }
  if (normalizedUpper === 'DISTANCE' || normalizedUpper.includes('MILE')) {
    return 'Mileage';
  }
  if (normalizedUpper === 'CWT' || normalizedUpper.includes('HUNDREDWEIGHT')) {
    return 'CWT';
  }
  if (normalizedUpper === 'TON' || normalizedUpper === 'TONS') {
    return 'Ton';
  }

  return '';
};

// Builds rate type select options, always including the normalized current value
// so the dropdown displays correctly when the backend sends codes or LLM aliases.
export const getAljexRateTypeSelectOptions = (
  currentValue: Undef<string>,
  allowedLabels: readonly string[] = ALJEX_RATE_TYPE_LABELS
): { value: string; label: string }[] => {
  const normalizedCurrent = normalizeAljexRateType(currentValue);
  const labels = new Set<string>([...allowedLabels]);
  if (normalizedCurrent) {
    labels.add(normalizedCurrent);
  }
  return Array.from(labels).map((label) => ({ value: label, label }));
};

// Normalizes a single-select value from the Select component for rate type fields.
export const normalizeAljexRateTypeFromSelect = (
  value: Undef<string | number>
): string => {
  if (value == null || value === '') {
    return '';
  }
  const asString = String(value);
  return normalizeAljexRateType(asString) || asString;
};

export function mapAljexRateTypeToCode(rateType: string): string {
  return ALJEX_RATE_TYPE_LABEL_TO_CODE[rateType] ?? rateType;
}

export type AljexStopAddressFields = {
  name?: MaybeUndef<string>;
  addressLine1?: MaybeUndef<string>;
  city?: MaybeUndef<string>;
  state?: MaybeUndef<string>;
  zip?: MaybeUndef<string>;
};

// True when address fields are filled but no saved TMS location is selected.
export function isAljexCustomStopLocation(
  externalTMSID: MaybeUndef<string>,
  address: MaybeUndef<AljexStopAddressFields>
): boolean {
  if (externalTMSID?.trim() || !address) {
    return false;
  }

  return Boolean(
    address.name?.trim() ||
    address.addressLine1?.trim() ||
    address.zip?.trim() ||
    (address.city?.trim() && address.state?.trim())
  );
}
/**
 * Extracts numeric trailer footage for Aljex from raw transport/equipment text.
 * Only two-digit matches: `specifications.transportSize` is capped at 2 chars in Aljex UI.
 * Examples:
 * - "FTL Van 53'" -> "53"
 * - "FTL Van or Reefer 53'" -> "53"
 * - "FTL Van or Reefer" -> ""
 */
export const extractAljexFootageFromTransportSize = (
  transportSize: Undef<string>
): string => {
  if (!transportSize) return '';

  const match = transportSize.match(/\b(\d{2})\b/);
  return match ? match[1] : '';
};
